import React, { useState } from 'react'
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Typography,
  Select,
  Tooltip,
  DatePicker,
  Badge,
  Popconfirm,
  Tabs,
  Drawer,
  Steps,
  List,
  Avatar,
  Row,
  Col,
  Divider,
  message,
  theme
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  UserOutlined,
  ShoppingOutlined
} from '@ant-design/icons'
import { formatCurrency, formatDate } from '../../../InventoryItems/utils/helpers'
import { mockSuppliers } from '../../../InventoryItems/utils/mockInventoryData'
import { purchaseOrderStatuses } from '../../utils/constants'
import useApprovals from '../../hooks/useApprovals'
import PurchaseOrderForm from '../PurchaseOrderForm'
import OrderApprovalModal from '../OrderApprovalModal'
import moment from 'moment'
import '../../../../styles/InventoryComponents.css'
import { hexToRGBA } from '../../../../../lib/utils'

const { Title, Text } = Typography
const { Option } = Select
const { RangePicker } = DatePicker
const { TabPane } = Tabs
const { Step } = Steps

const PurchaseOrdersList = () => {
  const {
    orders,
    filteredOrders,
    selectedOrder,
    loading,
    error,
    createPurchaseOrder,
    updatePurchaseOrder,
    deletePurchaseOrder,
    changeOrderStatus,
    pendingApprovals,
    canApprove,
    approveOrder,
    rejectOrder,
    getOrderApprovalStatus,
    isAddModalVisible,
    isEditModalVisible,
    isViewModalVisible,
    isApprovalModalVisible,
    showAddModal,
    hideAddModal,
    showEditModal,
    hideEditModal,
    showViewModal,
    hideViewModal,
    showApprovalModal,
    hideApprovalModal,
    searchTerm,
    handleSearch,
    filterByStatus,
    filterBySupplier,
    filterByDepartment,
    filterByDateRange,
    resetFilters
  } = useApprovals()

  // Local state for table settings
  const [pageSize, setPageSize] = useState(10)
  const [sortedInfo, setSortedInfo] = useState({})
  const [activeTab, setActiveTab] = useState('all')

  // Use theme token
  const { token } = theme.useToken()

  // Define table columns
  const columns = [
    {
      title: 'Order #',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <a onClick={() => showViewModal(orders.find((o) => o.id === text))}>{text}</a>,
      sorter: (a, b) => a.id.localeCompare(b.id),
      sortOrder: sortedInfo.columnKey === 'id' && sortedInfo.order
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
      sortOrder: sortedInfo.columnKey === 'supplierName' && sortedInfo.order,
      filters: mockSuppliers.map((sup) => ({ text: sup.name, value: sup.name })),
      onFilter: (value, record) => record.supplierName === value
    },
    {
      title: 'Date',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.orderDate) - new Date(b.orderDate),
      sortOrder: sortedInfo.columnKey === 'orderDate' && sortedInfo.order
    },
    {
      title: 'Items',
      dataIndex: 'itemsOrdered',
      key: 'itemsCount',
      render: (items) => items.length,
      sorter: (a, b) => a.itemsOrdered.length - b.itemsOrdered.length,
      sortOrder: sortedInfo.columnKey === 'itemsCount' && sortedInfo.order
    },
    {
      title: 'Total',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value) => formatCurrency(value),
      sorter: (a, b) => a.totalCost - b.totalCost,
      sortOrder: sortedInfo.columnKey === 'totalCost' && sortedInfo.order
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      sorter: (a, b) => a.department.localeCompare(b.department),
      sortOrder: sortedInfo.columnKey === 'department' && sortedInfo.order
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusObj = purchaseOrderStatuses.find((s) => s.value === status)
        return <Tag color={statusObj ? statusObj.color : 'default'}>{status}</Tag>
      },
      filters: purchaseOrderStatuses.map((status) => ({ text: status.value, value: status.value })),
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => a.status.localeCompare(b.status),
      sortOrder: sortedInfo.columnKey === 'status' && sortedInfo.order
    },
    {
      title: 'Approval',
      key: 'approval',
      render: (_, record) => {
        const approvalStatus = getOrderApprovalStatus(record)
        return (
          <Tooltip title={approvalStatus.status}>
            {approvalStatus.completedPercentage !== undefined ? (
              <Badge
                count={`${approvalStatus.completedPercentage}%`}
                style={{ backgroundColor: approvalStatus.color }}
              />
            ) : (
              <Badge count={approvalStatus.status} style={{ backgroundColor: approvalStatus.color }} />
            )}
          </Tooltip>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size='small' className='inventory-table-actions'>
          <Tooltip title='View Details'>
            <Button type='text' icon={<EyeOutlined />} onClick={() => showViewModal(record)} />
          </Tooltip>

          {/* Only show edit for Draft status */}
          {record.status === 'Draft' && (
            <Tooltip title='Edit'>
              <Button type='text' icon={<EditOutlined />} onClick={() => showEditModal(record)} />
            </Tooltip>
          )}

          {/* Show approve button if user can approve */}
          {canApprove(record) && (
            <Tooltip title='Approve'>
              <Button
                type='text'
                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                onClick={() => showApprovalModal(record)}
              />
            </Tooltip>
          )}

          {/* Only allow deletion of Draft orders */}
          {record.status === 'Draft' && (
            <Tooltip title='Delete'>
              <Popconfirm
                title='Are you sure you want to delete this order?'
                onConfirm={() => deletePurchaseOrder(record.id)}
                okText='Yes'
                cancelText='No'
              >
                <Button type='text' danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  // Handle table change
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter)
  }

  // Generate tab filters
  const renderTabs = () => {
    return (
      <Tabs activeKey={activeTab} onChange={setActiveTab} className='dashboard-tabs'>
        <TabPane tab='All Orders' key='all' />
        <TabPane tab='Draft' key='Draft' />
        <TabPane tab='Pending' key='Pending' />
        <TabPane tab='Approved' key='Approved' />
        <TabPane tab='Received' key='Received' />
        <TabPane
          tab={
            <Badge count={pendingApprovals.length} offset={[10, 0]}>
              Awaiting My Approval
            </Badge>
          }
          key='my-approvals'
        />
      </Tabs>
    )
  }

  // Filter orders by tab
  const getFilteredOrdersByTab = () => {
    if (activeTab === 'all') {
      return filteredOrders
    } else if (activeTab === 'my-approvals') {
      return pendingApprovals
    } else {
      return filteredOrders.filter((order) => order.status === activeTab)
    }
  }

  // Render order details drawer
  const renderOrderDetailsDrawer = () => {
    if (!selectedOrder) return null

    const approvalStatus = getOrderApprovalStatus(selectedOrder)

    return (
      <Drawer
        title={`Purchase Order: ${selectedOrder.id}`}
        width={600}
        onClose={hideViewModal}
        visible={isViewModalVisible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <Space>
            <Button onClick={hideViewModal}>Close</Button>
            {selectedOrder.status === 'Approved' && (
              <Button
                type='primary'
                onClick={() => {
                  changeOrderStatus(selectedOrder.id, 'Ordered')
                  message.success('Order status updated to Ordered')
                  hideViewModal()
                }}
              >
                Mark as Ordered
              </Button>
            )}
            {selectedOrder.status === 'Ordered' && (
              <Button
                type='primary'
                onClick={() => {
                  changeOrderStatus(selectedOrder.id, 'Received')
                  message.success('Order status updated to Received')
                  hideViewModal()
                }}
              >
                Mark as Received
              </Button>
            )}
            {canApprove(selectedOrder) && (
              <Button
                type='primary'
                onClick={() => {
                  hideViewModal()
                  showApprovalModal(selectedOrder)
                }}
              >
                Approve Order
              </Button>
            )}
          </Space>
        }
      >
        <Space direction='vertical' style={{ width: '100%' }}>
          <Card title='Order Information'>
            <Row gutter={16}>
              <Col span={12}>
                <p>
                  <strong>Supplier:</strong> {selectedOrder.supplierName}
                </p>
                <p>
                  <strong>Order Date:</strong> {formatDate(selectedOrder.orderDate)}
                </p>
                <p>
                  <strong>Department:</strong> {selectedOrder.department}
                </p>
                <p>
                  <strong>Cost Centre:</strong> {selectedOrder.costCentre}
                </p>
              </Col>
              <Col span={12}>
                <p>
                  <strong>Status:</strong>
                  <Tag color={purchaseOrderStatuses.find((s) => s.value === selectedOrder.status)?.color}>
                    {selectedOrder.status}
                  </Tag>
                </p>
                <p>
                  <strong>Expected Delivery:</strong>{' '}
                  {formatDate(selectedOrder.expectedDeliveryDate) || 'Not specified'}
                </p>
                <p>
                  <strong>Received Date:</strong> {formatDate(selectedOrder.receivedDate) || 'Not received'}
                </p>
              </Col>
            </Row>

            {selectedOrder.notes && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <p>
                  <strong>Notes:</strong> {selectedOrder.notes}
                </p>
              </>
            )}
          </Card>

          <Card title='Order Items'>
            <Table
              dataSource={selectedOrder.itemsOrdered}
              rowKey='itemId'
              pagination={false}
              size='small'
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'name',
                  key: 'name'
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity'
                },
                {
                  title: 'Unit Cost',
                  dataIndex: 'unitCost',
                  key: 'unitCost',
                  render: (cost) => formatCurrency(cost)
                },
                {
                  title: 'Total',
                  dataIndex: 'totalCost',
                  key: 'totalCost',
                  render: (cost) => formatCurrency(cost)
                }
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong>{formatCurrency(selectedOrder.totalCost)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>

          <Card title='Approval Workflow'>
            {selectedOrder.approvalChain && selectedOrder.approvalChain.length > 0 ? (
              <Steps
                direction='vertical'
                size='small'
                current={selectedOrder.approvalChain.filter((step) => step.status === 'Approved').length}
              >
                {selectedOrder.approvalChain.map((step, index) => (
                  <Step
                    key={index}
                    title={step.approverName}
                    status={step.status === 'Approved' ? 'finish' : step.status === 'Rejected' ? 'error' : 'wait'}
                    description={
                      <div>
                        <div>
                          Status:{' '}
                          <Tag
                            color={step.status === 'Approved' ? 'green' : step.status === 'Rejected' ? 'red' : 'blue'}
                          >
                            {step.status}
                          </Tag>
                        </div>
                        {step.timestamp && <div>Date: {formatDate(step.timestamp.split('T')[0])}</div>}
                      </div>
                    }
                  />
                ))}
              </Steps>
            ) : (
              <Text>No approval workflow defined for this order.</Text>
            )}
          </Card>
        </Space>
      </Drawer>
    )
  }

  return (
    <div className='inventory-page'>
      <div className='dashboard-header inventory-header'>
        <div className='dashboard-header-title'>
          <ShoppingOutlined className='dashboard-header-icon' />
          <Title level={4}>Purchase Orders</Title>
        </div>
        <Space>
          <Button
            icon={<PlusOutlined style={{ color: token.colorPrimary }} />}
            onClick={showAddModal}
            style={{
              background: hexToRGBA(token.colorPrimary, 0.1),
              borderColor: token.colorPrimary,
              color: token.colorPrimary,
              fontWeight: 'bold'
            }}
          >
            Create Order
          </Button>
        </Space>
      </div>

      <Card className='inventory-container'>
        {renderTabs()}

        <div className='inventory-action-bar'>
          <Input
            placeholder='Search orders...'
            prefix={<SearchOutlined />}
            allowClear
            className='inventory-search'
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <Space className='inventory-filter-group' wrap>
            <Select
              placeholder='Filter by Status'
              allowClear
              style={{ width: 150 }}
              onChange={(value) => filterByStatus(value)}
            >
              {purchaseOrderStatuses.map((status) => (
                <Option key={status.value} value={status.value}>
                  <Badge color={status.color} text={status.value} />
                </Option>
              ))}
            </Select>

            <Select
              placeholder='Filter by Supplier'
              allowClear
              style={{ width: 150 }}
              onChange={(value) => filterBySupplier(value)}
              showSearch
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {mockSuppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </Option>
              ))}
            </Select>

            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  filterByDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')])
                } else {
                  filterByDateRange(null)
                }
              }}
            />

            <Button icon={<SyncOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
        </div>

        <Table
          dataSource={getFilteredOrdersByTab()}
          columns={columns}
          rowKey='id'
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            pageSize: pageSize,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          className='inventory-table'
          size='middle'
        />
      </Card>

      {/* Purchase Order Form Modal */}
      <PurchaseOrderForm
        visible={isAddModalVisible || isEditModalVisible}
        onCancel={isAddModalVisible ? hideAddModal : hideEditModal}
        onSave={isAddModalVisible ? createPurchaseOrder : updatePurchaseOrder}
        title={isAddModalVisible ? 'Create Purchase Order' : 'Edit Purchase Order'}
        initialValues={isEditModalVisible ? selectedOrder : null}
      />

      {/* Order Details Drawer */}
      {renderOrderDetailsDrawer()}

      {/* Approval Modal */}
      <OrderApprovalModal
        visible={isApprovalModalVisible}
        onCancel={hideApprovalModal}
        onApprove={() => {
          approveOrder(selectedOrder.id)
          hideApprovalModal()
          message.success('Order approved successfully')
        }}
        onReject={() => {
          rejectOrder(selectedOrder.id)
          hideApprovalModal()
          message.success('Order rejected')
        }}
        order={selectedOrder}
      />
    </div>
  )
}

export default PurchaseOrdersList
